// The pure rules of the board's moves, drag and pins (spec 03 §3.6, §4.6,
// P3-I-3): a move's money in its currency, who may apply which move, where a
// dragged block lands and on which lane, the start a keyboard or a typed time
// asks for, the ghosts a preview draws, why an operation cannot be moved, and
// a preview's KPI delta as rows. No React and no network here, so each rule is
// unit-tested alone. The server decides every move; these keep the board from
// offering one it would refuse.

import { wallToUtcMs, zoneWall } from "./calendar-lib";
import type { TimeWindow } from "./schedule-lib";

// ---- money -------------------------------------------------------------------------

/**
 * Integer minor units in their currency, formatted for the locale. The
 * currency's own exponent sets the divisor (KZT and USD 2, JPY 0), and an
 * unknown code falls back to the minor figure with its code rather than
 * throwing inside a render.
 */
export function formatMoneyMinor(minor: number, currency: string, locale: string): string {
  try {
    const fmt = new Intl.NumberFormat(locale, { style: "currency", currency });
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    return fmt.format(minor / Math.pow(10, digits));
  } catch {
    return `${String(minor)} ${currency}`;
  }
}

/** What a move's money line says for this reader (§7 narrowing (b)). */
export type MoveMoney =
  | { readonly kind: "hidden" }
  | { readonly kind: "unpriced"; readonly reason: string }
  | {
      readonly kind: "priced";
      readonly cost: string;
      readonly atRisk: string | null;
      /**
       * Why the value at risk is not priced when the cost is, such as a push
       * of an order whose product has no standard cost (D-413 ruling 6).
       */
      readonly atRiskReason: string | null;
    };

/**
 * A move's money as the reader is served it. The money keys are absent by
 * key without `planning.cost.read`, which reads "Cost hidden"; a priced move
 * names its cost and the order value it puts at risk, or why that value has
 * no price; an unpriced one names why.
 */
export function moveMoney(
  move: {
    readonly cost_minor?: number | null | undefined;
    readonly value_at_risk_minor?: number | null | undefined;
    readonly currency?: string | undefined;
    readonly unpriced_reason?: string | null | undefined;
  },
  locale: string,
): MoveMoney {
  if (!("cost_minor" in move) || move.cost_minor === undefined) return { kind: "hidden" };
  if (move.cost_minor === null || move.currency === undefined) {
    return { kind: "unpriced", reason: move.unpriced_reason ?? "no_rate" };
  }
  const atRisk =
    move.value_at_risk_minor === null || move.value_at_risk_minor === undefined
      ? null
      : formatMoneyMinor(move.value_at_risk_minor, move.currency, locale);
  const atRiskReason =
    atRisk === null && move.value_at_risk_minor === null ? (move.unpriced_reason ?? null) : null;
  return {
    kind: "priced",
    cost: formatMoneyMinor(move.cost_minor, move.currency, locale),
    atRisk,
    atRiskReason,
  };
}

// ---- who may apply which move ------------------------------------------------------

export type ApplyGate = "allowed" | "no_publish" | "overtime_codes" | "offline" | "applied";

/**
 * Whether the reader may apply a move (§4.6, D-402 ruling 5): a holder of
 * `planning.schedule.publish`, and for an overtime move also of
 * `planning.calendar.manage` and `planning.cost.read`; never offline, and
 * never on an overload that already has its move applied.
 */
export function applyGate(
  kind: "overtime" | "alternate_work_center" | "push_order",
  has: (code: string) => boolean,
  state: { readonly offline: boolean; readonly applied: boolean },
): ApplyGate {
  if (state.applied) return "applied";
  if (!has("planning.schedule.publish")) return "no_publish";
  if (kind === "overtime" && (!has("planning.calendar.manage") || !has("planning.cost.read"))) {
    return "overtime_codes";
  }
  if (state.offline) return "offline";
  return "allowed";
}

// ---- why an operation cannot be moved ----------------------------------------------

export type MoveBlock = "in_progress" | "outsourced" | "on_hold";

/**
 * Why an operation's block cannot be dragged or moved (§3.6 step 4), or
 * undefined when it can: a started step holds its place (`OP_IN_PROGRESS`),
 * and a step on quality hold or an outsourced one holds no lane
 * (`OP_NOT_MOVABLE`, D-415 ruling 5).
 */
export function moveBlock(op: {
  readonly fixed: string | null;
  readonly is_outsourced: boolean;
  readonly unplaced_reason: string | null;
}): MoveBlock | undefined {
  if (op.fixed === "in_progress") return "in_progress";
  if (op.is_outsourced) return "outsourced";
  if (op.unplaced_reason === "on_hold") return "on_hold";
  return undefined;
}

// ---- where a drag lands ------------------------------------------------------------

/** The minutes a dragged or stepped start snaps to. */
export const SNAP_MINUTES = 15;
const MINUTE_MS = 60_000;

/** An instant rounded to the nearest whole `step` minutes. */
export function snapMs(ms: number, step = SNAP_MINUTES): number {
  const unit = step * MINUTE_MS;
  return Math.round(ms / unit) * unit;
}

/**
 * The start a drag asks for: the block's start moved by the pointer's travel
 * across the lane's track, as a share of the window's length, snapped to
 * {@link SNAP_MINUTES}. A track with no width answers the start unchanged.
 */
export function draggedStart(
  startMs: number,
  dxPx: number,
  trackWidthPx: number,
  window: Pick<TimeWindow, "startMs" | "endMs">,
): number {
  if (trackWidthPx <= 0) return startMs;
  const span = window.endMs - window.startMs;
  return snapMs(startMs + (dxPx / trackWidthPx) * span);
}

/** The lane under the pointer: the first whose band holds `y`, or undefined. */
export function laneAt(
  y: number,
  bands: readonly { readonly id: string; readonly top: number; readonly bottom: number }[],
): string | undefined {
  return bands.find((b) => y >= b.top && y < b.bottom)?.id;
}

/** Whether a lane is one the operation may be moved to (the server decides, D-416). */
export function laneAllowed(
  op: { readonly work_center_id: string; readonly allowed_work_center_ids: readonly string[] },
  laneId: string,
): boolean {
  return laneId === op.work_center_id || op.allowed_work_center_ids.includes(laneId);
}

// ---- a typed start -----------------------------------------------------------------

/** An instant as the facility's wall time for a `datetime-local` box, "2026-10-12T08:00". */
export function zonedInputValue(ms: number, timezone: string): string {
  return zoneWall(ms, timezone) ?? new Date(ms).toISOString().slice(0, 16);
}

const LOCAL_INPUT = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/u;

/** A `datetime-local` value read as the facility's wall time, or undefined when malformed. */
export function zonedInputMs(value: string, timezone: string): number | undefined {
  const m = LOCAL_INPUT.exec(value.trim());
  if (m === null) return undefined;
  const [, date, time] = m;
  if (date === undefined || time === undefined) return undefined;
  const ms = wallToUtcMs(date, time, timezone);
  return Number.isFinite(ms) ? ms : undefined;
}

/** An instant as the API takes a start: ISO with its offset, on a whole minute. */
export function startIso(ms: number): string {
  return new Date(Math.floor(ms / MINUTE_MS) * MINUTE_MS).toISOString();
}

// ---- the preview's ghosts and figures ----------------------------------------------

export interface Ghost {
  readonly op_ref: string;
  readonly laneId: string;
  readonly startMs: number;
  readonly endMs: number;
  /** The operation the drag moved, against the others the solve moved with it. */
  readonly primary: boolean;
}

/**
 * The ghost blocks a preview draws (§3.6 step 3): each moved operation at its
 * new start on its new lane, as long on the clock as the board shows it now.
 * An operation the board does not hold (outside the window) or one the
 * preview leaves unplaced draws no ghost.
 */
export function ghostsOf(
  preview: {
    readonly op_ref: string;
    readonly moved_ops: readonly {
      readonly op_ref: string;
      readonly new_start: string | null;
      readonly new_work_center_id: string | null;
    }[];
  },
  operations: readonly {
    readonly op_ref: string;
    readonly planned_start: string | null;
    readonly planned_end: string | null;
  }[],
): Ghost[] {
  const byRef = new Map(operations.map((o) => [o.op_ref, o]));
  const out: Ghost[] = [];
  for (const moved of preview.moved_ops) {
    const op = byRef.get(moved.op_ref);
    if (op === undefined || moved.new_start === null || moved.new_work_center_id === null) continue;
    if (op.planned_start === null || op.planned_end === null) continue;
    const length = Date.parse(op.planned_end) - Date.parse(op.planned_start);
    const startMs = Date.parse(moved.new_start);
    if (!Number.isFinite(length) || !Number.isFinite(startMs)) continue;
    out.push({
      op_ref: moved.op_ref,
      laneId: moved.new_work_center_id,
      startMs,
      endMs: startMs + Math.max(0, length),
      primary: moved.op_ref === preview.op_ref,
    });
  }
  return out;
}

export type KpiKey =
  | "late_orders"
  | "total_late_days"
  | "total_setup_minutes"
  | "overloads"
  | "unplaced"
  | "makespan_hours";

export const KPI_KEYS: readonly KpiKey[] = [
  "late_orders",
  "total_late_days",
  "total_setup_minutes",
  "overloads",
  "unplaced",
  "makespan_hours",
];

/** A KPI that grows is worse for every figure the board shows. */
export type DeltaTone = "worse" | "better" | "same";

/** The preview's KPI delta as rows: its signed figure and whether it is worse or better. */
export function kpiRows(delta: {
  readonly late_orders: number;
  readonly total_late_days: number;
  readonly total_setup_minutes: number;
  readonly overloads: number;
  readonly unplaced: number;
  readonly makespan_hours: string;
}): { key: KpiKey; value: number; tone: DeltaTone }[] {
  return KPI_KEYS.map((key) => {
    const value = key === "makespan_hours" ? Number(delta.makespan_hours) : delta[key];
    const safe = Number.isFinite(value) ? value : 0;
    return { key, value: safe, tone: safe > 0 ? "worse" : safe < 0 ? "better" : "same" };
  });
}

/** A signed figure for reading: "+2", "−1.5", "0". */
export function signed(value: number, locale: string): string {
  const fmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  if (value > 0) return `+${fmt.format(value)}`;
  if (value < 0) return `−${fmt.format(-value)}`;
  return fmt.format(0);
}
